import React from "react";
import { Container, Grid } from "@mui/material";
import { StyledSectionContainer } from "styles/components/SectionContainer/index.styles";
import { StyledSectionContainerColorWrapper } from "styles/components/SectionContainer/index.styles";
import { StyledFaqContainer } from "index.styles";
import { getFaqsByCategories } from "utils/helpers/getFaqData";

/**
 * Custom hook for dynamic page rendering based on content configuration
 * @param {Object} content - Array of sections to render
 * @param {Object} componentMap - Mapping of component type strings to React components
 * @param {Array} spreadPropsComponents - Array of component types that expect props to be spread directly
 * @param {Object} additionalContext - Additional context to pass to components (e.g., filteredFaqData)
 * @returns {Array} - Array of React elements ready to render
 */
const useDynamicPageRenderer = (content, componentMap, spreadPropsComponents = [], additionalContext = {}) => {
  const renderSubComponent = (componentData, isLeft = true, sectionGridProps = null) => {
    if (!componentData) return null;

    const SubComponent = componentMap[componentData.componentType];
    if (!SubComponent) return null;

    const defaultGridProps = isLeft ? { xs: 12, md: 5, lg: 4 } : { xs: 12, md: 6, lg: 7 };
    const gridProps = componentData.gridProps || sectionGridProps || defaultGridProps;

    // Handle FAQ component with dynamic data injection
    let props = { ...componentData };

    if (componentData.componentType === "Faq" && componentData.content) {
      // Check if we have filtered FAQ data from search
      if (additionalContext.filteredFaqData && additionalContext.filteredFaqData.length > 0) {
        // Use filtered data if available
        props = {
          ...componentData,
          content: {
            ...componentData.content,
            faqSections: additionalContext.filteredFaqData,
          },
        };
      }
      // Otherwise, check if faqCategories is specified
      else if (componentData.content.faqCategories && !componentData.content.faqSections) {
        const faqSource = componentData.content.faqSource || "all";
        const dynamicFaqSections = getFaqsByCategories(componentData.content.faqCategories, faqSource);

        props = {
          ...componentData,
          content: {
            ...componentData.content,
            faqSections: dynamicFaqSections,
          },
        };
      }
    }

    return (
      <Grid item {...gridProps} order={componentData.order}>
        <SubComponent {...props} />
      </Grid>
    );
  };

  const renderColumn = (columnData, isLeft, section) => {
    if (!columnData) return null;

    const isArray = Array.isArray(columnData);
    const data = isArray ? { items: columnData } : columnData;

    if (Array.isArray(data.items)) {
      const containerGridProps = data.gridProps || section.gridProps || (isLeft ? { xs: 12, md: 5, lg: 4 } : { xs: 12, md: 6, lg: 7 });

      return (
        <Grid item {...containerGridProps} order={data.order}>
          <Grid container direction="column" rowSpacing={data.rowSpacing ?? section.rowGap ?? 2}>
            {data.items.map((item, idx) => (
              <React.Fragment key={idx}>{renderSubComponent(item, isLeft, { xs: 12 })}</React.Fragment>
            ))}
          </Grid>
        </Grid>
      );
    }

    return renderSubComponent(columnData, isLeft, section.gridProps);
  };

  const groupedComponents = [];
  let backgroundComponents = [];

  if (!content || !Array.isArray(content)) {
    console.warn("useDynamicPageRenderer: Content is not a valid array", content);
    return [];
  }

  content.forEach(({ section }, index) => {
    const Component = componentMap[section?.componentType];
    const props = { ...section };
    let componentElement;

    if (!Component) {
      console.warn(`Component type "${section?.componentType}" not found in componentMap`);
      return;
    }

    if (section?.componentType === "TwoColumnSection") {
      const leftComponent = renderColumn(section.leftColumn, true, section);
      const rightComponent = renderColumn(section.rightColumn, false, section);
      const bottomComponent = section.bottomContent ? renderSubComponent(section.bottomContent, false, { xs: 12 }) : null;

      componentElement = (
        <StyledSectionContainer cssProps={section.cssProps} key={index} id={`two-column-section-${index}`}>
          <Component
            {...props}
            leftComponent={leftComponent}
            rightComponent={rightComponent}
            bottomComponent={bottomComponent}
            direction={section.direction || (section.columnReverse ? "row-reverse" : "row")}
          />
        </StyledSectionContainer>
      );
    } else if (spreadPropsComponents.includes(section?.componentType)) {
      componentElement = <Component key={index} {...props} />;
    } else if (section?.componentType === "Faq") {
      // Handle direct FAQ component with dynamic data
      let faqProps = { ...props };

      // Check if we have filtered FAQ data from search
      if (additionalContext.filteredFaqData && additionalContext.filteredFaqData.length > 0) {
        faqProps = {
          ...props,
          content: {
            ...section.content,
            faqSections: additionalContext.filteredFaqData,
          },
        };
      }
      // Otherwise check if faqCategories is specified
      else if (section.content?.faqCategories && !section.content?.faqSections) {
        const faqSource = section.content.faqSource || "all";
        const dynamicFaqSections = getFaqsByCategories(section.content.faqCategories, faqSource);

        faqProps = {
          ...props,
          content: {
            ...section.content,
            faqSections: dynamicFaqSections,
          },
        };
      }

      componentElement = <Component key={index} {...faqProps} content={faqProps.content} />;
    } else {
      componentElement = <Component key={index} {...props} content={section} cardType={section.cardType} />;
    }

    if (section?.componentType === "Faq") {
      componentElement = <StyledFaqContainer key={`faq-${index}`}>{componentElement}</StyledFaqContainer>;
    }
    if (section?.componentType === "SearchBarComponent") {
      componentElement = <Container sx={{ marginTop: "2rem" }}>{componentElement}</Container>;
    }

    if (section.colorWrapper) {
      backgroundComponents.push(componentElement);
    } else {
      if (backgroundComponents.length > 0) {
        groupedComponents.push(<StyledSectionContainerColorWrapper key={`bg-${index}`}>{backgroundComponents}</StyledSectionContainerColorWrapper>);
        backgroundComponents = [];
      }
      groupedComponents.push(componentElement);
    }
  });

  if (backgroundComponents.length > 0) {
    groupedComponents.push(<StyledSectionContainerColorWrapper key="bg-end">{backgroundComponents}</StyledSectionContainerColorWrapper>);
  }

  return groupedComponents;
};

export default useDynamicPageRenderer;
